import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Converts a native JS Date to a plain "yyyy-MM-dd" string using LOCAL date components
 * (getFullYear/getMonth/getDate) — never toISOString()/toJSON(), which convert to UTC first
 * and shift the calendar day back by one for any positive UTC offset (e.g. IST) whenever the
 * local time is past midnight-minus-offset (i.e. always, since date-pickers bind local midnight).
 * Formalizes the pattern already proven correct in shipments.component.ts's toYMD() and
 * account-details.component.ts's inline getters — use this instead of reinventing either one.
 */
export function toLocalDateString(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  // Several existing form models type a picker-bound field as `string` even though PrimeNG
  // always actually puts a Date there while the form is live — pass an already-formatted
  // string straight through rather than forcing every call site to cast.
  if (typeof date === 'string') return date;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Same as toLocalDateString but includes local hours/minutes/seconds — for fields that carry
 * a time-of-day component (no trailing "Z"/offset — this is intentionally NOT a UTC instant). */
export function toLocalDateTimeString(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  if (typeof date === 'string') return date;
  const datePart = toLocalDateString(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${datePart}T${hh}:${mm}:${ss}`;
}

/**
 * Parses a "yyyy-MM-dd" (or "yyyy-MM-ddTHH:mm") string — as read from a native
 * <input type="date"|"datetime-local">.value — into a local-midnight/local-time Date object.
 * Never use `new Date(dateOnlyString)` directly: per the JS spec, a bare "yyyy-MM-dd" string
 * (no time/offset) is parsed as UTC midnight, which displays as the PREVIOUS day in any
 * negative-of-UTC... actually any timezone behind UTC shows next day, any ahead shows same/prev
 * depending on local offset — the point is it's UTC-anchored, not local-anchored, and this
 * function avoids that entirely by constructing the Date from parsed components directly.
 */
export function parseLocalDateString(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  if (timePart) {
    const [hh, mm] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hh || 0, mm || 0);
  }
  return new Date(year, month - 1, day);
}

/**
 * Reactive-forms validator: rejects a date-of-birth value less than `minYears` ago (i.e. the
 * person would be younger than `minYears`). Accepts either a Date (from p-datepicker/native
 * input binding) or a "yyyy-MM-dd"/"dd/MM/yyyy" string (some DOB forms store it pre-formatted).
 * Returns { underMinAge: { minYears } } so callers can render a single shared message.
 */
export function minAgeValidator(minYears: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    let dob: Date | null = null;
    if (value instanceof Date) {
      dob = value;
    } else if (typeof value === 'string') {
      const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        dob = new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
      } else {
        dob = parseLocalDateString(value);
      }
    }
    if (!dob || Number.isNaN(dob.getTime())) return null;

    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - minYears);
    // dob must be ON OR BEFORE cutoff to be old enough (dob > cutoff means younger than minYears)
    return dob > cutoff ? { underMinAge: { minYears } } : null;
  };
}
